<!-- Displays a message to tell the user their order has been successful. Allows them to download any related eticket PDFs. Requires the response from the booking query that contains the trips with etickets to show the eticket data. -->
<script setup lang="ts">
import { toRefs, onMounted, ref } from "vue";
import type { Ref } from "vue";
import type { FulfilmentRequestResponse } from "@/models/checkout/FulfilmentRequestModel";
import { useBookingData } from "@/stores/booking-storage";
import { usePaymentStore } from "@/stores/payment";
import { getCurrentUser } from "aws-amplify/auth";
import _get from "lodash/get";

const props = defineProps<{
    bookingData: FulfilmentRequestResponse;
}>();
const { bookingData } = toRefs(props);
const reference: Ref<string | undefined> = ref("");
const etickets: Ref<Array<string>> = ref([]);
const bookingStore = useBookingData();
const paymentStore = usePaymentStore();
const emailAddress: Ref<string> = ref("");

// Get the users email address, if they are logged in then use that, otherwise use the email address from the guest checkout.
async function getUserEmail(): Promise<string> {
    try {
        const { signInDetails } = await getCurrentUser();
        if (!_get(signInDetails, "loginId", false) || signInDetails === undefined) {
            throw new Error();
        } else {
            // This should never return a string because it will of been thrown to the catch block below
            return signInDetails.loginId ?? "";
        }
    } catch (error) {
        // If an error occurs, it means the user is not logged in
        if (paymentStore.guestUserEmail) {
            return paymentStore.guestUserEmail;
        }
        return "";
    }
}

// Downloads the provided PDF file to the device.
// @param pdfData - The eticket object from the API. Contains a base64 encoded string to download under the "file" attribute.
function downloadPdf(pdfObj) {
    const { pdfData } = pdfObj;
    const blob = new Blob([Uint8Array.from(atob(pdfData), (c) => c.charCodeAt(0))], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${pdfObj.utn}.pdf`;
    a.click();
}

onMounted(async () => {
    emailAddress.value = await getUserEmail();
    bookingStore.clearBookingData();
    reference.value = bookingData.value.todCollectionReference;
    etickets.value = bookingData.value.trips
        .flatMap((trip) => trip.fares)
        .flatMap((fare) => fare.tickets)
        .flatMap((ticket) => ticket.etickets);
});
</script>

<template>
    <div class="text-center pt-15 pb-15">
        <font-awesome-icon icon="fa-circle-check" class="fa-lg orange"></font-awesome-icon>
        <h2 class="pt-5">Your booking is complete</h2>
        <div>Check your inbox at {{ emailAddress }}</div>
        <div>for an email containing confirmation of your booking</div>
        <div>If it's not there within 15 minutes, and you've checked your <i>Spam</i>, please contact <a href="mailto:info@mytrainticket.co.uk">customer services</a></div>
        <div class="pt-5" v-if="reference !== ''">Your ticket collection reference is: {{ reference }}</div>

        <template v-if="etickets.length !== 0">
            <h3 class="mt-10">Download your e-tickets</h3>
            <div class="download-etickets-wrapper">
                <v-btn v-for="pdf in etickets" :key="pdf.id" class="primary-button mt-2" @click="downloadPdf(pdf)"> {{ pdf.utn + ".pdf" }} </v-btn>
            </div>
        </template>
    </div>
</template>

<style scoped lang="scss">
svg.svg-inline--fa.fa-circle-check.fa-lg.orange {
    scale: 2;
}
.v-container {
    line-height: 2;
}
.v-btn {
    text-transform: none;
}
.download-etickets-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    column-gap: 22px;
    row-gap: 13px;
    max-width: 600px;
    flex-wrap: wrap;
    margin: 0 auto;
}
</style>
